"use client";

import { useEffect, useState, useTransition } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { AttendanceStatus } from "@prisma/client";
import { usePathname, useSearchParams } from "next/navigation";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import GenericAuth from "../auth/GenericAuth";
import { toastHandler } from "../ToastHandler";
import { If } from "../v3/Conditional";
import { Button } from "../v3/themed/Button";
import Modal from "../v3/themed/Modal";
import {
    DefaultUserValues,
    getRSVPStepsAndPageData,
} from "./actions/rsvp-steps-and-data";
import { handleMultiStepRSVPFlow } from "./actions/rsvp-steps-resolver";
import BuyMembershipOrTicket from "./steps-content/BuyMembershipOrTicket";
import JoinWaitlistStep from "./steps-content/JoinWailistStep";
import QuestionnaireModalContent, {
    QuestionnaireEssential,
} from "./steps-content/QuestionnaireModalContent";
import QuickRegisterModalContent from "./steps-content/QuickRegisterModalContent";
import TermsAndConditionsModalContent from "./steps-content/TermsAndConditionsModalContent";
import {
    ClientSideSteps,
    ProfileEditRequiredData,
    RSVPStepsEnum,
    RSVPStepsFormInputData,
} from "./validators";

import Link from "next/link";
import { RiExternalLinkLine, RiTicket2Line } from "react-icons/ri";
import { UserTicketSheet } from "~/app/beta/events/[eventSlug]/_components/UserTicketSheet";
import { getWhitelabeledCommunity } from "~/server/api/helpers/communityHelpers";
import { RSVPWrapperCustomization } from "~/types/CustomizableLayout";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { arraysAreEqual } from "~/utils/various";
import { AddToCalendarButtons } from "../themed/AddToCalendar";
import { DialogTitle } from "../v3/themed/Dialog";
import GlassButton from "../v4/themed/GlassButton";
import AddAdditionalGuestsOfUserModalContent from "./steps-content/AddAdditionalGuestsOfUserModalContent";
import AttendanceLimitReachedModalContent from "./steps-content/AttendanceLimitReachedModal";
import CheckSubscriptionButton from "./steps-content/CheckSubscriptionButton";
import MissingDataModalContent from "./steps-content/MissingDataModalContent";
import QuickRegisterVerifyCodeModalContent from "./steps-content/QuickRegisterVerifyCodeModalContent";
import { TicketPurchaseOptions } from "./steps-content/TicketPurchaseOptions";
import UnRSVPButtons from "./unRSVPButtons";

type TermsItem = NonNullable<EventBySlugOutput["community"]>["terms"][number];

type Props = {
    eventSlug: string;
    steps: ClientSideSteps[];
    allSteps: RSVPStepsEnum[];
    questionnaires: QuestionnaireEssential[] | null;
    texts?: {
        mainRSVPButton?: string;
        lastStepSubmit?: string;
    };
    communityTerms?: {
        title?: string;
        description?: string;
        terms: TermsItem[];
    };
    eventInfo: Awaited<ReturnType<typeof getRSVPStepsAndPageData>>["eventInfo"];
    membershipList: Awaited<
        ReturnType<typeof getRSVPStepsAndPageData>
    >["membershipList"];
    missingUserData: ProfileEditRequiredData | null | undefined;
    defaultUserValues: DefaultUserValues;
    refetchAction?: () => Promise<void>;
    customization?: RSVPWrapperCustomization;
};

const RSVP_MODAL_OPEN_URL_KEY = "rsvpModOpen";

const RSVPModalStepControl = ({
    eventSlug,
    questionnaires,
    communityTerms,
    eventInfo,
    membershipList,
    refetchAction,
    customization,
    ...props
}: Props) => {
    const pathname = usePathname();
    const params = useSearchParams();

    const form = useForm<RSVPStepsFormInputData>({
        defaultValues: {
            eventSlug,
            questionnaires: questionnaires?.map((questionnaire) => ({
                questionnaireId: questionnaire?.id,
                answersv2: Array.from(
                    Array(questionnaire?.questionsv2.length),
                ).map((_, index) => ({
                    text: "",
                    questionId: questionnaire?.questionsv2[index]?.id || "",
                })),
            })),
            terms: communityTerms?.terms?.map((t) => ({
                id: t.id,
                isRequired: t.isRequired,
                text: t.text,
                checked: false,
            })),
        },
    });

    const [isTicketSheetOpen, setIsTicketSheetOpen] = useState(false);
    const [steps, setSteps] = useState(props.steps);
    const [allSteps, setAllSteps] = useState(props.allSteps);
    const [currentStep, setCurrentStep] = useState<RSVPStepsEnum>();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [attendeeStatus, setAttendeeStatus] = useState<AttendanceStatus>();
    const [isWaitingForMagicLink, setIsWaitingForMagicLink] = useState(false);
    const [
        hasOpenedExtenralSubscriptionPage,
        setHasOpenedExtenralSubscriptionPage,
    ] = useState(false);
    const refreshWhosGoing = api.useUtils().event.getBySlug.refetch;
    const [isRefreshing, startRefreshing] = useTransition();

    const injectNewSteps = <T extends RSVPStepsEnum>(
        currentSteps: T[],
        updatedSteps: T[],
        index: number,
    ) => {
        const newSteps: T[] = [];
        for (let [i, currentStep] of currentSteps.entries()) {
            if (i > index) break;
            newSteps.push(currentStep);
            // remove steps we already have
            updatedSteps.indexOf(currentStep) !== -1 &&
                updatedSteps.splice(updatedSteps.indexOf(currentStep), 1);
        }
        return [...newSteps, ...updatedSteps];
    };

    useEffect(() => {
        // steps have changed (for example user logged in)

        // special case - subscription required but user already has subscription,
        // we refreshed data with router.refresh()
        if (
            steps[currentStepIndex] === "SUBSCRIPTION_REQUIRED" &&
            props.steps.length === 0
        ) {
            setIsOpen(false);
            setSteps(props.steps);
            form.handleSubmit(onSubmit)();
            return;
        }
        if (
            !arraysAreEqual(props.steps, steps) ||
            !arraysAreEqual(props.allSteps, allSteps)
        ) {
            // extend steps and allSteps with new steps
            setSteps(injectNewSteps(steps, props.steps, currentStepIndex));
            setAllSteps(
                injectNewSteps(allSteps, props.allSteps, currentStepIndex),
            );
        } else setCurrentStep(steps[currentStepIndex]);
    }, [props.steps, props.allSteps, currentStepIndex]);

    useEffect(() => {
        const rsvpModOpen = params?.get(RSVP_MODAL_OPEN_URL_KEY);
        if (rsvpModOpen && rsvpModOpen === "true") {
            if (allSteps.length === 1 && allSteps.includes("ATTEND")) {
                form.handleSubmit(onSubmit)();
            } else {
                setIsOpen(true);
            }
        }
    }, [params]);

    useEffect(() => {
        const purchaseTickets = params?.get("purchaseTickets");
        if (purchaseTickets) {
            setIsOpen(true);
        }
    }, [params]);

    const updateStatus = async (attendeeStatus?: AttendanceStatus) => {
        // Reloads the "who's going" data. For some reason, just revalidating wasn't working :/
        await refetchAction?.(); // revalidates data from beta/events/[eventSlug] page
        refreshWhosGoing();
        setAttendeeStatus(attendeeStatus);
    };

    const onSubmit = async (data: RSVPStepsFormInputData) => {
        await toastHandler({
            loadingMessage: "Processing RSVP...",
            successMessage: "RSVP submitted successfully",
            operation: async () => {
                const result = await handleMultiStepRSVPFlow(data);
                if (result?.success === false) {
                    throw new Error(result.message);
                }
                updateStatus(result?.data?.attendeeStatus);
            },
        });

        setIsOpen(false);
        setCurrentStep(undefined);
    };

    const setStep = (index: number) => {
        setCurrentStepIndex(index);
        setCurrentStep(steps[index]);
    };

    const nextStep = () => {
        const nextStepIndex = currentStepIndex + 1;
        if (currentStepIndex === steps.length - 1) {
            setIsOpen(false);
            return;
        }
        setStep(nextStepIndex);
    };

    const prevStep = () => {
        const prevStepIndex = currentStepIndex - 1;
        if (currentStepIndex === 0) {
            setIsOpen(false);
            return;
        }
        setStep(prevStepIndex);
    };

    if (allSteps.includes("ATTENDING") || attendeeStatus === "ATTENDING") {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <div className="flex items-center justify-between gap-2">
                    <h6
                        className={cn("text-xl", customization?.labelClassName)}
                    >
                        You're in!
                    </h6>
                    <If condition={!!eventInfo.event?.isTimeSet}>
                        <div className="flex items-center gap-1">
                            <AddToCalendarButtons
                                event={eventInfo.event}
                                customization={customization?.buttons}
                            />
                            {eventInfo.attendee && (
                                <>
                                    <GlassButton
                                        size="xs"
                                        variant="dark"
                                        className={cn(
                                            "rounded-md text-white",
                                            customization?.buttons?.secondary,
                                        )}
                                        onClick={() =>
                                            setIsTicketSheetOpen(true)
                                        }
                                    >
                                        <RiTicket2Line className="h-4 w-4" />
                                        Ticket details
                                    </GlassButton>
                                    {eventInfo.event && (
                                        <UserTicketSheet
                                            event={eventInfo.event}
                                            attendee={eventInfo.attendee}
                                            isOpen={isTicketSheetOpen}
                                            setIsOpen={setIsTicketSheetOpen}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </If>
                </div>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    Invite friends or share this event on social media!
                </p>
                <div className="flex items-center justify-between">
                    <span>Rethinking your attendance?</span>
                </div>
                <UnRSVPButtons
                    eventId={eventInfo?.event?.id}
                    onUpdateStatus={updateStatus}
                    customization={customization}
                />
            </div>
        );
    }

    if (allSteps.includes("IN_WAITLIST") || attendeeStatus === "WAITLIST") {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn("text-xl", customization?.labelClassName)}>
                    On the waitlist
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    We'll send you an email notification if space opens up.
                </p>
            </div>
        );
    }

    if (
        allSteps.includes("AWAITING_HOST_APPROVAL") ||
        attendeeStatus === "PENDING_APPROVAL"
    ) {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn("text-xl", customization?.labelClassName)}>
                    Waiting approval
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    Your request hasn't been approved yet. Please wait for the
                    host to approve your request.
                </p>
            </div>
        );
    }

    return (
        <>
            <Button
                className={cn(
                    "w-full bg-v3-clover text-white",
                    customization?.buttons?.default,
                )}
                onClick={() => {
                    // Join event immidiately if there's only one step
                    if (allSteps.length === 1 && allSteps.includes("ATTEND")) {
                        form.handleSubmit(onSubmit)();
                    } else if (
                        steps.length === 0 &&
                        allSteps.includes("PAY_ONCE")
                    ) {
                        form.handleSubmit(onSubmit)();
                    } else if (
                        steps.length === 0 &&
                        allSteps.includes("REQUEST_TO_JOIN")
                    ) {
                        form.handleSubmit(onSubmit)();
                    } else {
                        setIsOpen(true);
                    }
                }}
            >
                {(() => {
                    if (steps.includes("MEMBERSHIP_ATTENDANCE_LIMIT_REACHED")) {
                        return "Attend (Membership limit reached)";
                    }
                    if (allSteps.includes("REQUEST_TO_JOIN"))
                        return "Request to join";
                    if (steps.includes("JOIN_WAITLIST")) return "Join waitlist";

                    if (allSteps.includes("PAY_ONCE")) {
                        return (
                            <span className="flex items-center justify-center gap-1">
                                {eventInfo.eventPrice} Buy ticket
                            </span>
                        );
                    }
                    if (allSteps.includes("SELECT_TICKET")) {
                        return (
                            <span className="flex items-center justify-center gap-1">
                                Get Tickets
                            </span>
                        );
                    }
                    if (allSteps.includes("BUY_TICKET")) {
                        return (
                            <span className="flex items-center justify-center gap-1">
                                Buy ticket
                            </span>
                        );
                    }
                    if (allSteps.includes("BUY_MEMBERSHIP_OR_TICKET")) {
                        return (
                            <span className="flex items-center justify-center gap-1">
                                Join event for $
                                {
                                    eventInfo.eventTicketPackage
                                        ?.ticketTiers?.[0]?.priceDollars
                                }
                            </span>
                        );
                    }

                    return "Attend";
                })()}
            </Button>
            <FormProvider {...form}>
                <Modal
                    customization={customization}
                    open={isOpen}
                    onOpenChange={(isOpen) => {
                        setIsOpen(isOpen);
                        // remove param when closing modal
                        if (!isOpen && params) {
                            const searchParams = new URLSearchParams(
                                params as URLSearchParams,
                            );
                            searchParams.delete(RSVP_MODAL_OPEN_URL_KEY);
                            window.history.replaceState(
                                null,
                                "",
                                `?${searchParams.toString()}`,
                            );
                        }
                    }}
                    header={
                        <div className="mt-8 flex flex-col items-start gap-2">
                            <DialogTitle
                                className={cn(
                                    "text-[20px] text-white",
                                    customization?.labelClassName,
                                )}
                            >
                                Join event
                            </DialogTitle>
                            <span
                                className={cn(
                                    "text-sm text-v3-N-300",
                                    customization?.descriptionClassName,
                                )}
                            >
                                {currentStepIndex + 1} / {steps.length}
                            </span>
                        </div>
                    }
                    footer={
                        <If condition={currentStep !== "REGISTER_VERIFY_CODE"}>
                            <div className="flex w-full flex-col items-end justify-end gap-2">
                                <If
                                    condition={
                                        steps.length > 1 &&
                                        currentStep !== "LOGIN"
                                    }
                                >
                                    <div className="mb-2 flex w-full justify-center gap-2 py-2">
                                        {steps.map((_, index) => (
                                            <div
                                                key={index}
                                                className={`h-2 rounded-full transition-all duration-300 ${
                                                    currentStepIndex >= index
                                                        ? cn(
                                                              "w-6 bg-v3-primary",
                                                              customization
                                                                  ?.buttons
                                                                  ?.default,
                                                          )
                                                        : cn(
                                                              "w-6 bg-v3-N-400",
                                                              customization
                                                                  ?.buttons
                                                                  ?.secondary,
                                                          )
                                                }`}
                                            />
                                        ))}
                                    </div>
                                </If>
                                <If condition={!isWaitingForMagicLink}>
                                    <div className="flex flex-row justify-end space-x-2">
                                        <If
                                            condition={
                                                currentStepIndex !== 0 &&
                                                currentStep !==
                                                    "SUBSCRIPTION_REQUIRED"
                                            }
                                        >
                                            <Button
                                                className={cn(
                                                    "w-[140px]",
                                                    customization?.buttons
                                                        ?.secondary,
                                                )}
                                                variant="tertiary"
                                                onClick={() => {
                                                    prevStep();
                                                }}
                                            >
                                                Back
                                            </Button>
                                        </If>

                                        <If
                                            condition={
                                                currentStepIndex !==
                                                    steps.length - 1 &&
                                                currentStep !== "LOGIN"
                                            }
                                        >
                                            <Button
                                                variant="secondary"
                                                className={cn(
                                                    "w-[140px]",
                                                    customization?.buttons
                                                        ?.default,
                                                )}
                                                disabled={isNextDisabled}
                                                onClick={() => {
                                                    nextStep();
                                                }}
                                            >
                                                Next
                                            </Button>
                                        </If>

                                        <If
                                            condition={
                                                currentStep ===
                                                "SUBSCRIPTION_REQUIRED"
                                            }
                                        >
                                            <CheckSubscriptionButton
                                                hasChecked={
                                                    hasOpenedExtenralSubscriptionPage
                                                }
                                            />
                                            {!hasOpenedExtenralSubscriptionPage && (
                                                <Button
                                                    className={cn(
                                                        customization?.buttons
                                                            ?.default,
                                                    )}
                                                    onClick={() => {
                                                        setHasOpenedExtenralSubscriptionPage(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    <Link
                                                        href={
                                                            getWhitelabeledCommunity(
                                                                eventInfo.event
                                                                    ?.community
                                                                    ?.slug,
                                                            )
                                                                ?.subscriptionUrl ||
                                                            ""
                                                        }
                                                        target="_blank"
                                                        className="flex items-center gap-1"
                                                    >
                                                        Upgrade subscription
                                                        <RiExternalLinkLine className="h-4 w-4" />
                                                    </Link>
                                                </Button>
                                            )}
                                        </If>

                                        <If
                                            condition={
                                                ![
                                                    "SELECT_TICKET",
                                                    "SUBSCRIPTION_REQUIRED",
                                                ].includes(currentStep ?? "") &&
                                                currentStepIndex ===
                                                    steps.length - 1
                                            }
                                        >
                                            <Button
                                                disabled={isNextDisabled}
                                                className={cn(
                                                    customization?.buttons
                                                        ?.default,
                                                )}
                                                onClick={() => {
                                                    form.handleSubmit(
                                                        onSubmit,
                                                    )();
                                                }}
                                            >
                                                {(() => {
                                                    if (
                                                        allSteps.includes(
                                                            "REQUEST_TO_JOIN",
                                                        )
                                                    )
                                                        return "Request to join";
                                                    if (
                                                        steps.includes(
                                                            "JOIN_WAITLIST",
                                                        )
                                                    )
                                                        return "Join waitlist";
                                                    if (
                                                        allSteps.includes(
                                                            "BUY_TICKET",
                                                        ) ||
                                                        allSteps.includes(
                                                            "PAY_ONCE",
                                                        )
                                                    ) {
                                                        return "Buy ticket";
                                                    }
                                                    if (
                                                        steps.includes(
                                                            "BUY_MEMBERSHIP_OR_TICKET",
                                                        )
                                                    ) {
                                                        return "Proceed to checkout";
                                                    }

                                                    return "Join event";
                                                })()}
                                            </Button>
                                        </If>
                                    </div>
                                </If>
                            </div>
                        </If>
                    }
                >
                    <If condition={currentStep === "LOGIN"}>
                        <GenericAuth
                            mode="login"
                            variant="modal"
                            callbackUrl={`${pathname}?${RSVP_MODAL_OPEN_URL_KEY}=true`}
                            hideTitle
                            allowGuests
                            useInPlaceFlow
                            setIsWaitingForMagicLink={setIsWaitingForMagicLink}
                            customization={customization}
                        />
                    </If>

                    <If condition={currentStep === "REGISTER"}>
                        <QuickRegisterModalContent
                            setNextDisabled={setIsNextDisabled}
                            customization={customization}
                        />
                    </If>

                    <If condition={currentStep === "SELECT_TICKET"}>
                        <TicketPurchaseOptions
                            onNext={nextStep}
                            data={form.getValues()}
                            eventSlug={eventSlug}
                        />
                    </If>

                    <If condition={currentStep === "REGISTER_VERIFY_CODE"}>
                        <QuickRegisterVerifyCodeModalContent
                            customization={customization}
                            //Handle submit doensnt work here for some reason
                            setNextDisabled={setIsNextDisabled}
                            onFinish={async () => {
                                if (
                                    !allSteps.some((step) =>
                                        [
                                            "SELECT_TICKET",
                                            "SUBSCRIPTION_REQUIRED",
                                        ].includes(step),
                                    )
                                ) {
                                    const data = form.getValues();
                                    await toastHandler({
                                        loadingMessage: "Processing RSVP...",
                                        successMessage:
                                            "RSVP submitted successfully",
                                        operation: async () => {
                                            const result =
                                                await handleMultiStepRSVPFlow(
                                                    data,
                                                );
                                            if (result?.success === false) {
                                                throw new Error(result.message);
                                            }
                                            updateStatus(
                                                result?.data?.attendeeStatus,
                                            );
                                        },
                                    });

                                    setIsOpen(false);
                                    setCurrentStep(undefined);
                                } else {
                                    nextStep();
                                }
                            }}
                        />
                    </If>

                    <If condition={currentStep === "MISSING_DATA"}>
                        <MissingDataModalContent
                            onNext={nextStep}
                            setCurrentStep={setCurrentStep}
                            eventSlug={eventSlug}
                            setNextDisabled={setIsNextDisabled}
                            attendeeMissingData={props.missingUserData}
                            customization={customization}
                        />
                    </If>

                    <If
                        condition={
                            currentStep ===
                            "MEMBERSHIP_ATTENDANCE_LIMIT_REACHED"
                        }
                    >
                        <AttendanceLimitReachedModalContent
                            nextAttendanceDateInfo={
                                eventInfo.nextAttendanceDateInfo
                            }
                        />
                    </If>

                    <If condition={currentStep === "ANSWER_QUESTIONNAIRE"}>
                        <QuestionnaireModalContent
                            questionnaires={questionnaires}
                            setNextDisabled={setIsNextDisabled}
                            defaultUserValues={props.defaultUserValues}
                        />
                    </If>

                    <If condition={currentStep === "TERMS_CONFIRMATION"}>
                        {communityTerms && (
                            <TermsAndConditionsModalContent
                                communityTerms={communityTerms}
                                setNextDisabled={setIsNextDisabled}
                            />
                        )}
                    </If>

                    <If condition={currentStep === "JOIN_WAITLIST"}>
                        <JoinWaitlistStep
                            setIsNextDisabled={setIsNextDisabled}
                            eventInfo={eventInfo}
                            customization={customization}
                        />
                    </If>

                    <If
                        condition={
                            currentStep === "BUY_TICKET" ||
                            currentStep === "MEMBERSHIP_REQUIRED" ||
                            currentStep === "BUY_MEMBERSHIP_OR_TICKET"
                        }
                    >
                        <BuyMembershipOrTicket
                            steps={allSteps}
                            membershipList={membershipList}
                            setIsNextDisabled={setIsNextDisabled}
                            onNext={nextStep}
                            eventTicketPackage={eventInfo.eventTicketPackage}
                            hasManagerPermission={
                                eventInfo.hasManagerPermission
                            }
                            eventPrice={eventInfo.eventPrice}
                            eventResourceTags={eventInfo?.event?.resourceTags}
                        />
                    </If>

                    <If condition={currentStep === "SUBSCRIPTION_REQUIRED"}>
                        <div className={customization?.descriptionClassName}>
                            This event is exclusive to paid subscribers of The
                            Free Press. Subscribe now to unlock access.
                        </div>
                    </If>

                    <If
                        condition={
                            currentStep === "ADD_ADDITIONAL_GUESTS_OF_USER"
                        }
                    >
                        <AddAdditionalGuestsOfUserModalContent
                            setIsNextDisabled={setIsNextDisabled}
                            eventTicketPackage={eventInfo.eventTicketPackage}
                        />
                    </If>
                </Modal>
            </FormProvider>
        </>
    );
};

export default RSVPModalStepControl;
